import { useState, useRef } from 'react'
import {
  View, Text, TouchableOpacity, Modal, Image,
  StyleSheet, Animated, PanResponder, SafeAreaView,
} from 'react-native'
import { useTranslation } from 'react-i18next'
import Ionicons from '@expo/vector-icons/Ionicons'
import { supabase } from '../../lib/supabase'
import { colors, fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import GradientButton from '../GradientButton'
import { WebView } from 'react-native-webview'
import { LOGO } from '../../lib/assets'

export default function OnboardingOverlay({ userId, onDone, replay = false }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = makeStyles(tc)
  const [step, setStep] = useState(0)
  const [rulesOpen, setRulesOpen] = useState(false)
  const opacity = useRef(new Animated.Value(1)).current

  const ONBOARDING_STEPS = [
    {
      emoji: '👋',
      title: t('home.onboarding_step1_title'),
      body:  t('home.onboarding_step1_body'),
    },
    {
      emoji: '🚴',
      title: t('home.onboarding_step2_title'),
      items: [
        { icon: '🚴', text: t('home.onboarding_item1') },
        { icon: '🏆', text: t('home.onboarding_item2') },
        { icon: '⚔️', text: t('home.onboarding_item3') },
      ],
    },
    {
      emoji: '🏁',
      title: t('home.onboarding_step3_title'),
      body:  t('home.onboarding_step3_body'),
    },
  ]

  const total = ONBOARDING_STEPS.length
  const s = ONBOARDING_STEPS[step]

  const panResponder = useRef(PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder:  (_, g) => Math.abs(g.dx) > 10,
    onPanResponderRelease: (_, g) => {
      if (Math.abs(g.dx) > 40) {
        if (g.dx < 0 && step < total - 1) setStep(s => s + 1)
        if (g.dx > 0 && step > 0)         setStep(s => s - 1)
      }
    },
  })).current

  const handleDone = async () => {
    Animated.timing(opacity, { toValue: 0, duration: 350, useNativeDriver: false }).start(async () => {
      if (!replay) await supabase.from('dim_user').update({ onboarding_done: true }).eq('id_user', userId)
      onDone()
    })
  }

  return (
    <>
      {/* Modal Regolamento */}
      <Modal visible={rulesOpen} animationType="slide" onRequestClose={() => setRulesOpen(false)}>
        <View style={{ flex: 1, backgroundColor: tc.bg, paddingTop: 50 }}>
          <View style={{
            flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
            paddingHorizontal: 16, paddingVertical: 12,
            borderBottomWidth: 1, borderBottomColor: tc.border,
          }}>
            <Text style={{ fontFamily: fonts.condensedBold, fontSize: 16, color: tc.text, textTransform: 'uppercase' }}>
              {t('profile.rules')}
            </Text>
            <TouchableOpacity
              onPress={() => setRulesOpen(false)}
              style={{ backgroundColor: tc.bgCard2, borderWidth: 1, borderColor: tc.border, borderRadius: 8, width: 32, height: 32, alignItems: 'center', justifyContent: 'center' }}
            >
              <Ionicons name="close" size={16} color={tc.textMuted} />
            </TouchableOpacity>
          </View>
          <WebView source={{ uri: 'https://www.virtuacycling.com/rules.html' }} style={{ flex: 1 }} />
        </View>
      </Modal>

      {/* Onboarding */}
      <Modal visible transparent animationType="none">
        <Animated.View style={[styles.container, { backgroundColor: tc.bg, opacity }]} {...panResponder.panHandlers}>
          <SafeAreaView style={styles.inner}>

            {/* Logo */}
            <Image
              source={LOGO}
              style={styles.logo}
              resizeMode="contain"
            />

            {/* Contenuto step */}
            <View style={styles.content}>
              <Text style={styles.emoji}>{s.emoji}</Text>
              <Text style={[styles.title, { color: tc.text }]}>{s.title}</Text>

              {s.body && (
                <Text style={[styles.body, { color: tc.textMuted }]}>{s.body}</Text>
              )}

              {s.items && (
                <View style={styles.itemsList}>
                  {s.items.map((item, i) => (
                    <View key={i} style={[styles.itemCard, { backgroundColor: tc.bgCard, borderColor: tc.border }]}>
                      <Text style={styles.itemIcon}>{item.icon}</Text>
                      <Text style={[styles.itemText, { color: tc.text }]}>{item.text}</Text>
                    </View>
                  ))}
                </View>
              )}
            </View>

            {/* Dots */}
            <View style={styles.dots}>
              {ONBOARDING_STEPS.map((_, i) => (
                <TouchableOpacity key={i} onPress={() => setStep(i)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <View style={[
                    styles.dot,
                    { backgroundColor: i === step ? tc.rosa : tc.border },
                    i === step && styles.dotActive,
                  ]} />
                </TouchableOpacity>
              ))}
            </View>

            {/* Bottoni */}
            <View style={styles.buttons}>
              {step < total - 1 ? (
                <GradientButton onPress={() => setStep(s => s + 1)} size="lg" style={{ width: '100%' }}>
                  {t('home.onboarding_next')}
                </GradientButton>
              ) : (
                <>
                  <GradientButton onPress={handleDone} size="lg" style={{ width: '100%' }}>
                    {t('home.onboarding_start')}
                  </GradientButton>
                  <TouchableOpacity
                    style={[styles.rulesBtn, { borderColor: tc.border }]}
                    onPress={() => setRulesOpen(true)}
                  >
                    <Text style={[styles.rulesBtnText, { color: tc.textMuted }]}>{t('home.onboarding_rules')}</Text>
                  </TouchableOpacity>
                </>
              )}
              {step > 0 && (
                <TouchableOpacity onPress={() => setStep(s => s - 1)} style={styles.backBtn}>
                  <Text style={[styles.backBtnText, { color: tc.textMuted }]}>{t('common.back')}</Text>
                </TouchableOpacity>
              )}
            </View>

          </SafeAreaView>
        </Animated.View>
      </Modal>
    </>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container: { flex: 1 },
  inner: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 24,
    paddingTop: 40,
    paddingBottom: 32,
  },
  logo:      { width: 160, height: 50, opacity: 0.9 },
  content:   { flex: 1, alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: 400, gap: 20 },
  emoji:     { fontSize: 64, lineHeight: 72, textAlign: 'center' },
  title:     { fontFamily: fonts.condensedBold, fontSize: 26, textTransform: 'uppercase', textAlign: 'center', letterSpacing: 0.5 },
  body:      { fontSize: 15, lineHeight: 24, textAlign: 'center' },
  itemsList: { width: '100%', gap: 12 },
  itemCard:  { flexDirection: 'row', gap: 14, alignItems: 'flex-start', borderRadius: 12, borderWidth: 1, padding: 14, paddingHorizontal: 16 },
  itemIcon:  { fontSize: 22, flexShrink: 0, marginTop: 1 },
  itemText:  { flex: 1, fontSize: 14, lineHeight: 21 },
  dots:      { flexDirection: 'row', gap: 8, marginBottom: 24 },
  dot:       { height: 8, borderRadius: 4, width: 8 },
  dotActive: { width: 24 },
  buttons:      { width: '100%', maxWidth: 400, gap: 10 },
  rulesBtn:     { borderWidth: 1, borderRadius: 12, padding: 12, alignItems: 'center' },
  rulesBtnText: { fontFamily: fonts.condensedBold, fontSize: 14, letterSpacing: 0.5 },
  backBtn:      { alignItems: 'center', padding: 4 },
  backBtnText:  { fontSize: 13 },
})